<template>
  <ContextMenuWrapper placement="top">
    <component :is="showHighlighter ? ZtHighlightBox : useFragment" variant="wiggle" radius-class="rounded-10"
      ><ZtButtonContext data-testid="menu-share-expiration" :pre-label="expirationPreLabel" :label="expirationLabel" icon="general.stopwatch"
    /></component>
    <template #menu>
      <MenuLink @click="runAction({ expires: ItemExpiresMode.NEVER })" title="Never">Never</MenuLink>
      <MenuSeparator />
      <MenuLink
        v-for="option in timeBasedExpirationOptions"
        :key="option.label"
        @click="runAction({ expiresAt: expiresAtFromSeconds(option.seconds), expiresAfterViews: null })"
        :title="option.label"
      >
        {{ option.label }}
      </MenuLink>
      <MenuSeparator />
      <MenuLink @click="runAction({ expiresAt: null, expiresAfterViews: 1 })" title="after 1 view">after 1 view</MenuLink>
      <MenuLink @click="runAction({ expiresAt: null, expiresAfterViews: 10 })" title="after 10 views">after 10 views</MenuLink>
      <MenuSeparator />
      <MenuLink @click="runAction()" title="Custom settings...">Custom settings...</MenuLink>
    </template>
  </ContextMenuWrapper>
</template>
<script setup lang="ts">
import { useItemExpiration } from '@/modules/viewer/composables/useItemExpiration';
import { ItemExpiresMode, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { computed, ref } from 'vue';
import { useTrack, TrackClickEvents } from '@/composables/useTrack';
import { ZtHighlightBox, useFragment, ZtButtonContext, ContextMenuWrapper, MenuLink, MenuSeparator } from '@/storybook';
import { TIME_BASED_EXPIRATION_OPTIONS } from '@shared/expiration_options';

defineProps<{
  showHighlighter?: boolean;
}>();

const viewerItemStore = useViewerItemStore();
const { expiresInHuman } = useItemExpiration();
const { trackClick } = useTrack();

const emit = defineEmits(['customSettingEnabled']);

const runAction = (payload?: { expires?: ItemExpiresMode; expiresAt?: string | null; expiresAfterViews?: number | null }) => {
  if (!payload) {
    customSettingsEnabled.value = true;
    emit('customSettingEnabled');

    trackClick(TrackClickEvents.ITEM_EXPIRATION_SPECIFIC_DATE);
    return;
  }

  trackClick(TrackClickEvents.ITEM_EXPIRATION_AFTER);
  viewerItemStore.saveExpiration(payload);
  customSettingsEnabled.value = false;
};

const customSettingsEnabled = ref(false);
const expirationPreLabel = computed(() => (viewerItemStore.expired ? '' : 'Expires'));
const timeBasedExpirationOptions = TIME_BASED_EXPIRATION_OPTIONS;

const expirationLabel = computed(() => {
  if (viewerItemStore.expired) {
    return 'Item has expired';
  }
  if (customSettingsEnabled.value && expiresInHuman.value === 'never') {
    return '(Custom settings)';
  }
  if (expiresInHuman.value === 'never') {
    return 'Never';
  }

  return expiresInHuman.value;
});

const expiresAtFromSeconds = (seconds: number) => new Date(Date.now() + seconds * 1000).toISOString();
</script>
